import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material'
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import { useGetDonorSchedule, useCancelChairpersonAppointment } from '@/hooks'
import { CancelConfirmationDialog } from '@/components/donation'
import { formatDisplayTime } from '@/utils'
import { useThemeController } from '@/styles/theme/ThemeProvider'
import { hexToRgba } from '@/styles/theme/palette'

const DonorScheduleRowSkeleton = () => (
  <TableRow>
    <TableCell sx={{ width: 170, backgroundColor: 'grey.50' }}>
      <Skeleton variant='text' width={80} />
    </TableCell>
    <TableCell>
      <Skeleton variant='text' width={200} />
    </TableCell>
    <TableCell sx={{ width: 80 }}>
      <Skeleton variant='rounded' width={48} height={24} />
    </TableCell>
    <TableCell sx={{ width: 44 }} />
  </TableRow>
)

const buildPrintHtml = (
  schedule,
  driveInfo,
  driveDate,
  primaryHex,
  formatTime
) => {
  const primaryBg = hexToRgba(primaryHex, 0.08)
  const metaParts = [
    driveInfo?.hours ? `Hours: ${driveInfo.hours}` : null,
    driveInfo?.address ? `Address: ${driveInfo.address}` : null,
    driveInfo?.spotsAvailable != null
      ? `Appointments: ${driveInfo.spotsAvailable}`
      : null,
  ].filter(Boolean)

  const driveHeaderHtml = driveInfo
    ? `<div class="drive-header">
        ${driveInfo.name ? `<div class="drive-name">${driveInfo.name}</div>` : ''}
        ${driveDate ? `<div class="drive-date">${driveDate}</div>` : ''}
        ${metaParts.length ? `<div class="drive-meta">${metaParts.join(' &nbsp;•&nbsp; ')}</div>` : ''}
      </div>`
    : ''

  const rows = schedule
    .map(row => {
      const pt = row.slot_procedure_type ?? row.procedure_type
      const donor = row.donor ?? null
      const time = formatTime(row.start_time, 'h:mm a')

      const donorHtml = donor
        ? `<span class="donor-name">${donor.first_name} ${donor.last_name}</span>${
            donor.donor_number
              ? ` <span class="donor-number">(${donor.donor_number})</span>`
              : ''
          }`
        : ''

      const badgeHtml = pt
        ? `<span class="procedure-badge">${pt.short_description}</span>`
        : ''

      return `<tr>
        <td class="time-cell">${time}</td>
        <td class="donor-cell">${donorHtml}</td>
        <td class="procedure-cell">${badgeHtml}</td>
      </tr>`
    })
    .join('')

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Donor Schedule</title>
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 13px;
        color: #1a1a1a;
      }
      .drive-header {
        padding: 16px 20px 14px;
        border-bottom: 2px solid #e0e0e0;
      }
      .drive-name {
        font-size: 16px;
        font-weight: 600;
        color: ${primaryHex};
        margin-bottom: 4px;
      }
      .drive-date {
        font-size: 13px;
        font-weight: 600;
        margin-bottom: 4px;
      }
      .drive-meta {
        font-size: 12px;
        color: #555;
      }
      .schedule-title {
        padding: 10px 20px;
        font-size: 13px;
        font-weight: 600;
        color: ${primaryHex};
        border-top: 1px solid #e0e0e0;
        border-bottom: 1px solid #e0e0e0;
        background: ${primaryBg};
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }
      table { width: 100%; border-collapse: collapse; border-bottom: 1px solid #efefef; }
      tr { border-bottom: 1px solid #efefef; }
      td { padding: 9px 16px; vertical-align: middle; }
      .time-cell {
        width: 170px;
        font-size: 15px;
        font-weight: 600;
        background-color: #fafafa;
        white-space: nowrap;
      }
      .donor-name { font-weight: 600; }
      .donor-number { color: #777; }
      .procedure-badge {
        display: inline-block;
        padding: 2px 9px;
        background: #e8e8e8;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;
        letter-spacing: 0.02em;
      }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    </style>
  </head>
  <body>
    ${driveHeaderHtml}
    <div class="schedule-title">Donor Schedule</div>
    <table>${rows}</table>
  </body>
</html>`
}

export const DonorSchedule = ({ driveId, driveInfo, driveDate }) => {
  const { primaryHex } = useThemeController()
  const {
    donorSchedule,
    loadingDonorSchedule,
    donorScheduleError,
    getDonorSchedule,
  } = useGetDonorSchedule()
  const { cancelAppointment, loadingCancelAppointment } =
    useCancelChairpersonAppointment()
  const [selectedProcedureId, setSelectedProcedureId] = useState(null)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [appointmentToCancel, setAppointmentToCancel] = useState(null)

  useEffect(() => {
    getDonorSchedule(driveId)
  }, [driveId])

  const handleCancelClick = row => {
    setAppointmentToCancel(row.appointment_id)
    setCancelDialogOpen(true)
  }

  const handleConfirmCancel = async () => {
    if (!appointmentToCancel) return
    await cancelAppointment(appointmentToCancel)
    setCancelDialogOpen(false)
    setAppointmentToCancel(null)
    getDonorSchedule(driveId)
  }

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false)
    setAppointmentToCancel(null)
  }

  const procedureTypes = useMemo(() => {
    const map = new Map()
    donorSchedule.forEach(row => {
      const pt = row.slot_procedure_type ?? row.procedure_type
      if (pt && !map.has(pt.id)) map.set(pt.id, pt)
    })
    return Array.from(map.values()).sort((a, b) => a.sort_order - b.sort_order)
  }, [donorSchedule])

  const filteredSchedule = useMemo(() => {
    if (!selectedProcedureId) return donorSchedule
    return donorSchedule.filter(row => {
      const id = row.slot_procedure_type?.id ?? row.procedure_type?.id
      return id === selectedProcedureId
    })
  }, [donorSchedule, selectedProcedureId])

  const handleFilterClick = id => {
    setSelectedProcedureId(prev => (prev === id ? null : id))
  }

  return (
    <Box sx={{ mx: { xs: 1, md: 2 }, mt: { xs: 2, md: 3 } }}>
      {/* Procedure type filter chips */}
      {!loadingDonorSchedule && procedureTypes.length > 0 && (
        <Stack direction='row' sx={{ flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          {procedureTypes.map(pt => (
            <Chip
              key={pt.id}
              label={pt.short_description}
              onClick={() => handleFilterClick(pt.id)}
              color={selectedProcedureId === pt.id ? 'primary' : 'default'}
              variant={selectedProcedureId === pt.id ? 'filled' : 'outlined'}
              sx={{ fontWeight: 600, fontSize: '0.8125rem' }}
            />
          ))}
        </Stack>
      )}

      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'secondary.main',
          }}
        >
          <Typography variant='body1' fontWeight={700}>
            Donor Schedule
          </Typography>
          <Box
            component='button'
            onClick={() => {
              const html = buildPrintHtml(
                filteredSchedule,
                driveInfo,
                driveDate,
                primaryHex,
                formatDisplayTime
              )
              const win = window.open('', '_blank', 'width=820,height=700')
              win.document.write(html)
              win.document.close()
              win.focus()
              win.print()
              win.addEventListener('afterprint', () => win.close())
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'primary.main',
              fontSize: '0.875rem',
              fontWeight: 500,
              p: 0,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            <PrintOutlinedIcon sx={{ fontSize: 16 }} />
            Print Schedule
          </Box>
        </Box>

        {/* Schedule table */}
        <TableContainer>
          <Table size='small'>
            <TableBody>
              {loadingDonorSchedule ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <DonorScheduleRowSkeleton key={i} />
                ))
              ) : donorScheduleError ? (
                <TableRow>
                  <TableCell colSpan={4} align='center' sx={{ py: 4 }}>
                    <Typography variant='body2' color='error'>
                      {donorScheduleError?.response?.status === 403
                        ? "You don't have access to this drive's schedule."
                        : 'Unable to load the donor schedule.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredSchedule.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align='center' sx={{ py: 4 }}>
                    <Typography variant='body2' color='text.secondary'>
                      No schedule available
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSchedule.map((row, index) => {
                  const pt = row.slot_procedure_type ?? row.procedure_type
                  const donor = row.donor ?? null
                  const time = formatDisplayTime(row.start_time, 'h:mm a')

                  return (
                    <TableRow
                      key={row.slot_id ?? index}
                      sx={{
                        '&:last-child td': { border: 0 },
                        '&:hover': { backgroundColor: 'action.hover' },
                      }}
                    >
                      <TableCell
                        sx={{
                          padding: 1.7,
                          width: 200,
                          minWidth: 155,
                          whiteSpace: 'nowrap',
                          fontSize: '0.9375rem',
                          backgroundColor: 'grey.50',
                        }}
                      >
                        {time}
                      </TableCell>

                      <TableCell sx={{ py: 1 }}>
                        {donor && (
                          <Typography variant='body2'>
                            <Box component='span' fontWeight={500}>
                              {donor.first_name} {donor.last_name}
                            </Box>
                            {donor.donor_number ? (
                              <Box
                                component='span'
                                color='text.secondary'
                                sx={{ ml: 0.75 }}
                              >
                                ({donor.donor_number})
                              </Box>
                            ) : (
                              <Box
                                component='span'
                                color='text.secondary'
                                sx={{ ml: 0.75 }}
                              >
                                (Guest)
                              </Box>
                            )}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ width: 80, minWidth: 65 }}>
                        {pt && (
                          <Chip
                            label={pt.short_description}
                            size='small'
                            sx={{
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              backgroundColor: 'action.selected',
                              color: 'text.primary',
                              height: 24,
                            }}
                          />
                        )}
                      </TableCell>

                      <TableCell sx={{ width: 44, pr: 2 }}>
                        {donor && row.appointment_id && (
                          <IconButton
                            size='small'
                            sx={{ color: 'error.main', p: 0.5 }}
                            onClick={() => handleCancelClick(row)}
                          >
                            <DoNotDisturbOnOutlinedIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CancelConfirmationDialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        onConfirm={handleConfirmCancel}
        loading={loadingCancelAppointment}
      />
    </Box>
  )
}

DonorSchedule.propTypes = {
  driveId: PropTypes.string,
  driveInfo: PropTypes.object,
  driveDate: PropTypes.string,
}
